import i18n from '@/utils/i18n';
import React from 'react';
import {
  AddressInput,
  getAddressInputFromPlaceResult,
} from '@/components/addresses/address-input';
import { AddressInputType } from '@domain/addresses/types/types';
import { Button, Props as ButtonProps } from '@/components/button';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import {
  CONTAINER_MODAL_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import {
  ContainerModalStackParams,
  LocationManagerStackParams,
} from '@/navigators/types';
import { createStyles } from '@/contexts/theme-context';
import { FormInputContainer } from '@/components/form';
import { KeyboardAwareScrollView } from '@/components/with-keyboard-aware';
import { ListItem } from '@/components/list-item';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { SelectInput } from '@/components/select-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { Switch } from '@/components/switch';
import { TextArea } from '@/components/text-area';
import { TextInput } from '@/components/text-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from 'react-native';

interface Props {
  accountID: string;
  address: AddressInputType | null;
  address2: string;
  buttonProps: ButtonProps;
  buttonText: string;
  isProspect?: boolean;
  name: string;
  notes: string;
  onAccountIDChange: (newAccountID: string) => void;
  onAddress2Change: (newAddress2: string) => void;
  onAddressChange: (newAddress: AddressInputType | null) => void;
  onNameChange: (newName: string) => void;
  onNotesChange: (newNotes: string) => void;
  onRouteStopChange: (newRouteStop: string) => void;
  routeStop: string;
  setIsProspect?: (isProspect: boolean) => void;
}

type Navigation = CompositeNavigationProp<
  StackNavigationProp<
    LocationManagerStackParams,
    LOCATION_MANAGER_ROUTES.CreateAccount
  >,
  StackNavigationProp<
    ContainerModalStackParams,
    CONTAINER_MODAL_ROUTES.PlacesSearch
  >
>;

export const AccountForm: React.FC<Props> = ({
  accountID,
  address,
  address2,
  buttonProps,
  buttonText,
  isProspect,
  name,
  notes,
  routeStop,
  onAccountIDChange,
  onAddress2Change,
  onAddressChange,
  onNameChange,
  onNotesChange,
  onRouteStopChange,
  setIsProspect,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { navigate } = useNavigation<Navigation>();
  const [styles] = useStyles();

  return (
    <KeyboardAwareScrollView
      alwaysBounceVertical={false}
      contentContainerStyle={[
        styles.scrollViewContainer,
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
      keyboardShouldPersistTaps="handled"
    >
      <View style={styles.formContainer}>
        <FormInputContainer>
          <SelectInput
            containerStyle={styles.textInputContainer}
            label={`${i18n.t('business_name')}*`}
            onPress={() => {
              navigate(CONTAINER_MODAL_ROUTES.PlacesSearch, {
                creatable: true,
                initialValue: name,
                onSelect: (
                  placeName: string,
                  placeResult: google.maps.places.PlaceResult | null,
                ) => {
                  onNameChange(placeName);
                  if (placeResult) {
                    onAddressChange(
                      getAddressInputFromPlaceResult(placeResult),
                    );
                  }
                },
                placeholder: i18n.t('business_name'),
              });
            }}
            value={name}
          />
        </FormInputContainer>
        <AddressInput
          address2={address2}
          address2Props={{
            containerStyle: [
              styles.textInputContainer,
              styles.addressInputContainer,
            ],
          }}
          addressProps={{
            containerStyle: [
              styles.textInputContainer,
              styles.addressInputContainer,
            ],
          }}
          label={`${i18n.t('business_address')}*`}
          onAddress2Change={onAddress2Change}
          onAddressChange={onAddressChange}
          onOpenSearch={() =>
            navigate(CONTAINER_MODAL_ROUTES.PlacesSearch, {
              initialValue: address?.address || '',
              onSelect: (
                _,
                placeResult: google.maps.places.PlaceResult | null,
              ) => {
                if (placeResult) {
                  onAddressChange(
                    getAddressInputFromPlaceResult(placeResult, address2),
                  );
                }
              },
              placeholder: i18n.t('business_address'),
              types: ['address'],
            })
          }
          value={address}
        />
        <FormInputContainer>
          <TextInput
            autoCapitalize="none"
            autoCorrect={false}
            containerStyle={styles.textInputContainer}
            label={accountID ? i18n.t('account_id') : null}
            onChangeText={onAccountIDChange}
            placeholder={i18n.t('account_id')}
            value={accountID}
          />
        </FormInputContainer>
        <FormInputContainer>
          <TextInput
            autoCapitalize="none"
            autoCorrect={false}
            containerStyle={styles.textInputContainer}
            label={routeStop ? i18n.t('route_stop') : null}
            onChangeText={onRouteStopChange}
            placeholder={i18n.t('route_stop')}
            value={routeStop}
          />
        </FormInputContainer>
        <FormInputContainer>
          <TextArea
            containerStyle={styles.textInputContainer}
            label={i18n.t('notes')}
            numberOfLines={4}
            onChangeText={onNotesChange}
            placeholder={i18n.t('notes_placeholder')}
            style={TYPOGRAPHY.medium}
            value={notes}
          />
        </FormInputContainer>
        {/* only show prospect toggle when a screen wants to reference it */}
        {setIsProspect ? (
          <FormInputContainer>
            <ListItem
              item={{
                title: i18n.t('prospect'),
                value: 'prospect',
              }}
              listItemStyle={styles.listItem}
              renderRightComponent={() => (
                <Switch
                  onValueChange={(isProspect) => {
                    setIsProspect(isProspect);
                  }}
                  trackingData={{
                    name: 'prospect_switch',
                    properties: {
                      value: !isProspect,
                    },
                  }}
                  value={isProspect!}
                />
              )}
            />
          </FormInputContainer>
        ) : null}
      </View>
      <Button {...buttonProps}>{buttonText}</Button>
    </KeyboardAwareScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  addressInputContainer: {
    marginVertical: 8,
  },
  formContainer: {
    flex: 1,
  },
  listItem: {
    paddingHorizontal: 0,
  },
  scrollViewContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: SCREEN_PADDING,
  },
  textInputContainer: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
  },
}));
